import { useRef } from 'react';
import { buildApiUrl } from '../services/api';
import { PaymentFormData } from '../types/payment';

const normalizeDigits = (value: string) => value.replace(/\D/g, '');

export function useMercadoPago() {
  const mercadoPagoScriptRef = useRef<Promise<void> | null>(null);
  const mercadoPagoPublicKeyRef = useRef<Promise<string> | null>(null);
  const mercadoPagoInstanceRef = useRef<MercadoPagoInstance | null>(null);

  const getMercadoPagoPublicKey = async () => {
    if (!mercadoPagoPublicKeyRef.current) {
      mercadoPagoPublicKeyRef.current = (async () => {
        try {
          const response = await fetch(buildApiUrl('/mercadopago/public-key'));
          const data = await response.json().catch(() => null);
          const key = data?.publicKey || data?.public_key || data?.key;

          if (response.ok && key) {
            return key as string;
          }
        } catch (error) {
          console.warn('Não foi possível obter a chave pública Mercado Pago pela API.', error);
        }

        const envKey = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY;
        if (envKey) {
          return envKey;
        }

        throw new Error('Não foi possível obter a chave pública Mercado Pago');
      })();
    }

    try {
      return await mercadoPagoPublicKeyRef.current;
    } catch (error) {
      mercadoPagoPublicKeyRef.current = null;
      throw error;
    }
  };

  const loadMercadoPagoSdk = async () => {
    if (typeof window === 'undefined') {
      throw new Error('Ambiente inválido para tokenizar cartão');
    }

    if (window.MercadoPago) {
      return;
    }

    const sdkUrl = import.meta.env.VITE_MERCADOPAGO_JS_URL || 'https://sdk.mercadopago.com/js/v2';

    const loadScript = (url: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[data-mercadopago-sdk="true"][data-src="${url}"]`);
        if (existing) {
          const loaded = (existing as HTMLScriptElement).dataset.loaded === 'true';
          if (loaded) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Falha ao carregar Mercado Pago JS')));
          return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.dataset.mercadopagoSdk = 'true';
        script.dataset.src = url;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => {
          script.remove();
          reject(new Error('Falha ao carregar Mercado Pago JS'));
        };
        document.body.appendChild(script);
      });

    if (!mercadoPagoScriptRef.current) {
      mercadoPagoScriptRef.current = loadScript(sdkUrl);
    }

    await mercadoPagoScriptRef.current;

    if (!window.MercadoPago) {
      throw new Error('SDK do Mercado Pago não está disponível');
    }
  };

  const getMercadoPagoInstance = async () => {
    const publicKey = await getMercadoPagoPublicKey();
    await loadMercadoPagoSdk();

    if (!mercadoPagoInstanceRef.current) {
      const MercadoPagoConstructor = window.MercadoPago;
      if (!MercadoPagoConstructor) {
        throw new Error('SDK do Mercado Pago não está disponível');
      }

      mercadoPagoInstanceRef.current = new MercadoPagoConstructor(publicKey, {
        locale: 'pt-BR'
      });
    }

    return mercadoPagoInstanceRef.current;
  };

  const createCardToken = async (paymentData: PaymentFormData) => {
    const mercadoPago = await getMercadoPagoInstance();
    const expiry = paymentData.cardExpiry.split('/');
    const expMonth = (expiry[0] || '').padStart(2, '0');
    const expYearRaw = expiry[1] || '';
    const expYear = expYearRaw.length === 2 ? `20${expYearRaw}` : expYearRaw;

    if (!expMonth || !expYear) {
      throw new Error('Validade inválida');
    }

    const result = await mercadoPago.createCardToken({
      cardNumber: normalizeDigits(paymentData.cardNumber),
      cardholderName: paymentData.holderName,
      cardExpirationMonth: expMonth,
      cardExpirationYear: expYear,
      securityCode: normalizeDigits(paymentData.cardCvv),
      identificationType: 'CPF',
      identificationNumber: normalizeDigits(paymentData.holderDocument)
    });

    if (!result?.id) {
      throw new Error('Não foi possível tokenizar o cartão');
    }

    return result.id;
  };

  return {
    createCardToken
  };
}
